import type { ReactNode } from "react";
import { Link } from "react-router";
import { Banknote, CalendarDays, CheckCheck, FileSignature } from "lucide-react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { LinkButton } from "@/components/ui/LinkButton";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { FigureTile } from "@/components/data/FigureTile";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusChip } from "@/components/ui/StatusChip";
import {
  formatDateRange,
  formatDays,
  formatLongDate,
  formatMinutes,
  formatMoney,
  formatPayParts,
} from "@/lib/format";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useNavCounts } from "@/features/dashboard/hooks";
import { Inbox } from "@/features/dashboard/Inbox";
import { waitingOnMe } from "@/features/dashboard/waiting";
import { shownBalances } from "@/features/leave/balances";
import { useMyBalances, useMyHandovers, useMyLeaveRequests } from "@/features/leave/hooks";
import { ladderReading } from "@/features/leave/ladder";
import { leaveLength } from "@/features/leave/length";
import { leaveSalarySourceLabel } from "@/features/payroll/api";
import { useMyPay, useMyProfile } from "@/features/profile/hooks";
import { correctionsState } from "@/features/profile/corrections";
import { useMyOvertimeBalance } from "@/features/overtime/hooks";
import { useMyAssignments } from "@/features/schedule/hooks";
import { useMyDocuments } from "@/features/documents/hooks";
import { useMyTasks } from "@/features/separation/hooks";
import { useFilePreview } from "@/features/library/useFilePreview";
import { WeekAhead } from "@/features/rota/WeekAhead";

/**
 * What the two balances most people have are, and how they are counted. The
 * other types HR might choose to show carry no paragraph: their tile says the
 * number, which is all anybody asks of it.
 */
const TILE_HELP: Record<string, ReactNode> = {
  annual: (
    <>
      <p>
        Days of annual leave you can still ask for this year. It is credited on 1 January, in
        proportion to how much of the year you have worked here.
      </p>
      <p>
        Annual leave is taken between 1 June and 30 September. Your annual leave salary is
        paid with the salary of the month you joined, every year, so the days themselves are
        not paid again while you are away.
      </p>
    </>
  ),
  sick: (
    <>
      <p>
        Sick leave is a ladder, spent in order: the first 15 days in a year at full pay, the
        next 30 at half pay, and the rest unpaid.
      </p>
      <p>
        The figure is what is left on the rung you are on now; the line under it is what
        follows. Days you have asked for and not yet had answered count as spent.
      </p>
    </>
  ),
};

/**
 * The employee's landing page.
 *
 * Opens with whatever is waiting on them, as the same inbox HR has on its own
 * home, or one line saying that nothing is. Then the week, because "when am I
 * working" is what staff came for and it used to sit below three leave
 * balances and a fold. Then the figures they check once a month. Nothing else.
 */
export function HomeRoute() {
  const { session } = useAuth();
  const year = new Date().getFullYear();

  const { data: balances, isLoading: balancesLoading } = useMyBalances(year);
  const { data: overtime } = useMyOvertimeBalance();
  const { data: requests, isLoading: requestsLoading } = useMyLeaveRequests();
  const { data: assignments } = useMyAssignments();
  const { data: documents, isLoading: documentsLoading } = useMyDocuments();
  const preview = useFilePreview();
  const { data: pay } = useMyPay();
  // The sidebar asks for the same counts under the same key, so this is its
  // answer read a second time, not a second request.
  const { data: counts, isLoading: countsLoading } = useNavCounts("me");
  const { data: handovers, isLoading: handoversLoading } = useMyHandovers();
  // Their checklist, for any paper they still have to upload.
  const { data: checklist } = useMyTasks();
  // Corrections HR asked for, which leave the account open as it was.
  const { data: profile } = useMyProfile();
  const correcting = correctionsState(profile) === "with_me";

  // Only the types HR shows get a tile; the overtime figure is the third.
  // Three across is the common case and the grid only widens past it.
  const shown = shownBalances(balances ?? []);
  const wide = shown.length + 1 > 3;
  // "April", for the month the leave salary comes in every year.
  const leaveSalaryMonth = pay?.next_leave_salary_on
    ? formatLongDate(pay.next_leave_salary_on).replace(/^\d+ /, "").replace(/ \d{4}$/, "")
    : null;

  const toSign = (documents ?? []).filter((d) => d.my_status === "pending");
  const waiting = waitingOnMe({
    counts,
    documents,
    requests,
    handovers,
    isManager: session?.is_manager ?? false,
    today: today(),
    checklist,
    corrections: correcting
      ? { count: profile?.correctable_fields?.length ?? 0, note: profile?.corrections_note ?? null }
      : null,
  });
  // "Nothing is waiting on you" has to be true when it is said, so it is not
  // said until everything it is read from has answered.
  const gathering = countsLoading || documentsLoading || requestsLoading || handoversLoading;
  const userId = session?.user.id ?? "";
  const upcoming = (requests ?? [])
    .filter((r) => r.status === "pending" || (r.status === "approved" && r.end_date >= today()))
    .slice(0, 4);
  const nextCover = (assignments ?? [])[0];

  const firstName = session?.user.full_name_en.split(" ")[0] ?? "there";

  return (
    <div className="space-y-5">
      {/* The employee's landing page, and its title should be the size every
          other page's title is. It was set at card-heading size, which made
          the one screen most of the company opens first look like a section
          of something else. */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">Hello, {firstName}</h1>
        <p className="mt-1 text-base text-fg-muted">
          {session?.user.employee_id ? (
            <>
              Employee number <span className="u-ident">{session.user.employee_id}</span>
            </>
          ) : (
            "Your account is active."
          )}
        </p>
      </div>

      {correcting ? (
        <Alert tone="warning" title="HR asked you to correct some details">
          <p>
            {profile?.corrections_note ??
              "HR checked your record and marked what needs putting right."}
          </p>
          <LinkButton to="/profile/corrections" variant="secondary" size="sm" className="mt-3">
            Correct my details
          </LinkButton>
        </Alert>
      ) : null}

      {gathering ? (
        <Skeleton className="h-5 w-56" />
      ) : (
        <Inbox
          // A counter tablet is signed into by more than one person in a day.
          // What one of them put aside is kept under their own name, and read
          // again when somebody else signs in.
          key={userId}
          storageKey={`hrsys.home.inbox.cleared.${userId}`}
          items={waiting}
          describe={(live) =>
            live === 0
              ? "Everything here has been put aside. It is under Cleared."
              : "What needs you, most pressing first. Open a row to deal with it, or clear it to put it aside."
          }
          help={
            <>
              <p>
                Each row is something waiting on you: a document to sign, work a colleague has
                handed you, tasks, reviews to write. A number is how many there are; a mark is
                a single thing.
              </p>
              <p>
                Red has a date on it that has arrived and is under Important. Blue is ordinary
                work and grey is for you to know about; both are under Other.
              </p>
              <p>
                Clearing a row puts it aside for you alone, on this computer. It does not sign,
                answer or finish anything. A row with a number comes back by itself when the
                number changes, and whatever you have cleared is under Cleared.
              </p>
            </>
          }
          empty={
            <p className="flex items-center gap-2 text-base text-fg-muted">
              <CheckCheck className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
              Nothing is waiting on you.
            </p>
          }
        />
      )}

      <WeekAhead />

      {balancesLoading ? (
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
      ) : (
        <div className={wide ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-4" : "grid gap-4 sm:grid-cols-3"}>
          {shown.length === 0 ? (
            // HR has turned every tile off. Said in a line rather than left as
            // a gap, so nobody wonders whether their balances have gone.
            <p className="self-center text-base text-fg-muted sm:col-span-2">
              No leave balances are shown here. Every kind of leave can still be requested.
            </p>
          ) : (
            shown.map((balance) => {
              // A banded balance is read as the ladder, not as one total:
              // "90 days remaining" made ninety days sound like ninety paid
              // days, which they are not.
              const ladder = ladderReading(balance);
              const entitlement = balance.entitlement_days
                ? `of ${formatDays(balance.entitlement_days)} this year`
                : undefined;
              return (
                <FigureTile
                  key={balance.leave_type_id}
                  label={balance.leave_type_name}
                  value={ladder?.value ?? formatDays(balance.balance_days)}
                  unit={ladder?.unit ?? "days"}
                  help={TILE_HELP[balance.leave_type_code]}
                  caption={
                    ladder?.caption ??
                    (balance.paid_in_advance && leaveSalaryMonth && entitlement
                      ? `${entitlement} · leave salary every ${leaveSalaryMonth}`
                      : entitlement)
                  }
                />
              );
            })
          )}
          <FigureTile
            label="Overtime"
            value={formatMinutes(overtime?.balance_minutes ?? 0)}
            unit="h"
            caption="can cover unpaid leave"
            help={
              <>
                <p>
                  Hours worked beyond your contracted day, from the attendance terminal, that
                  have not been used up. On a day off or a public holiday the whole shift counts.
                </p>
                <p>
                  Unpaid leave draws on this balance first, a day's hours at a time, before any
                  day goes unpaid.
                </p>
              </>
            }
          />
        </div>
      )}

      {pay && pay.gross !== null ? (
        <Card
          title="Your pay"
          description="What HR has on your record. Only HR can change it."
          icon={<Banknote className="size-4" />}
          actions={
            <LinkButton to="/profile?tab=employment" variant="secondary" size="sm">
              Pay details
            </LinkButton>
          }
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <p className="u-label">A month</p>
              <p className="text-lg font-semibold tabular-nums text-fg">
                {formatMoney(pay.gross)}{" "}
                <span className="text-xs font-normal text-fg-subtle">AED</span>
              </p>
              <p className="text-xs text-fg-subtle">
                {formatPayParts(
                  pay.basic,
                  pay.allowances,
                  pay.other_pay,
                  pay.other_pay_note,
                  // Sent by the API before the type here has heard of it.
                  "cash_risk" in pay && typeof pay.cash_risk === "string" ? pay.cash_risk : null,
                )}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="u-label">Annual leave salary</p>
              {pay.leave_salary_month && pay.next_leave_salary_on ? (
                <>
                  <p className="text-base text-fg">
                    Paid every {leaveSalaryMonth}, with that month's salary. Next:{" "}
                    <span className="font-medium">
                      {formatLongDate(pay.next_leave_salary_on).replace(/^\d+ /, "")}
                    </span>
                    {pay.leave_salary_amount ? ` · ${formatMoney(pay.leave_salary_amount)} AED` : ""}
                  </p>
                  {/* Whether that figure was decided by HR or is simply a
                      month's pay. The same amount can be either, and the
                      difference matters to somebody comparing notes. */}
                  {pay.leave_salary_amount || pay.annual_leave_unpaid ? (
                    <p className="mt-0.5 text-xs text-fg-subtle">
                      {[
                        pay.leave_salary_amount
                          ? `${leaveSalarySourceLabel(pay.leave_salary_source)}.`
                          : null,
                        pay.annual_leave_unpaid
                          ? "Because it is paid in advance, the days you take as annual leave are not paid again in the month you are away."
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-base text-fg-subtle">
                  Paid with the salary of the month you joined, once HR has your joining date.
                </p>
              )}
            </div>
          </div>
        </Card>
      ) : null}

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Card
          title="Your leave"
          actions={
            <LinkButton
              to="/leaves/new"
              variant="secondary"
              size="sm"
              icon={<CalendarDays className="size-4" />}
            >
              Request leave
            </LinkButton>
          }
          flush={upcoming.length === 0}
        >
          {upcoming.length === 0 ? (
            <EmptyState
              title="No leave booked"
              description="Request time off and HR will review it."
            />
          ) : (
            <ul className="divide-y divide-line-faint">
              {upcoming.map((request) => (
                <li
                  key={request.id}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-base text-fg">{request.leave_type_name}</p>
                    <p className="mt-0.5 text-xs text-fg-subtle">
                      {formatDateRange(request.start_date, request.end_date)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="u-figure text-base text-fg-muted">
                      {leaveLength(request)}
                    </span>
                    <StatusChip status={request.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Where you are working" flush={!nextCover}>
          {nextCover ? (
            <div>
              <p className="text-base text-fg">
                Covering <span className="font-medium">{nextCover.branch_name}</span>
              </p>
              <p className="mt-1 text-base text-fg-muted">
                {formatDateRange(nextCover.start_date, nextCover.end_date)}
              </p>
              {nextCover.note ? (
                <p className="mt-2 text-base text-fg-subtle">{nextCover.note}</p>
              ) : null}
              <Link
                to="/schedule"
                className="mt-3 inline-block text-base text-accent hover:underline"
              >
                See your full schedule
              </Link>
            </div>
          ) : (
            <EmptyState
              title="At your own branch"
              description="You will be told here if you are needed elsewhere."
            />
          )}
        </Card>
      </div>

      {toSign.length === 0 && (documents ?? []).length > 0 ? (
        <Card title="Documents" flush>
          <ul className="divide-y divide-line-faint">
            {(documents ?? []).slice(0, 5).map((document) => (
              <li
                key={document.document_id}
                className="flex items-center justify-between gap-3 px-5 py-3"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <FileSignature className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
                  <span className="truncate text-base text-fg">{document.title}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {document.my_status === "signed" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Open ${document.title}`}
                      onClick={() =>
                        preview(`/me/documents/${document.document_id}/file`, document.title)
                      }
                    >
                      Open
                    </Button>
                  ) : null}
                  <StatusChip status={document.my_status} />
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
